import type { QueryClient } from "@tanstack/react-query";
import type { MailboxEvent } from "@mailflare/shared";

import { queryKeys } from "./query-keys";
import { patchMessagesInPages, removeMessagesFromPages, type MessagePages } from "./message-cache";

/**
 * Applies a server `MailboxEvent` to the TanStack Query cache.
 *
 * `message.created`/`message.updated` invalidate the affected lists (server
 * ordering is authoritative); flag and delete events are patched in place for
 * instant feedback. `sync.required` invalidates everything.
 */
export function applyMailboxEvent(queryClient: QueryClient, event: MailboxEvent): void {
  const listKey = queryKeys.messages(event.mailboxId);

  switch (event.type) {
    case "message.created":
    case "message.updated": {
      queryClient.invalidateQueries({ queryKey: listKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.message(event.message.id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.drafts });
      return;
    }
    case "message.flags_changed": {
      queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
        patchMessagesInPages(data, event.messageIds, {
          ...(event.isRead !== null ? { isRead: event.isRead } : {}),
          ...(event.isFlagged !== null ? { isFlagged: event.isFlagged } : {}),
          ...(event.folder !== null ? { folder: event.folder } : {}),
        }),
      );
      for (const id of event.messageIds) {
        queryClient.invalidateQueries({ queryKey: queryKeys.message(id) });
      }
      return;
    }
    case "message.deleted": {
      queryClient.setQueriesData<MessagePages>({ queryKey: listKey }, (data) =>
        removeMessagesFromPages(data, event.messageIds),
      );
      for (const id of event.messageIds) {
        queryClient.removeQueries({ queryKey: queryKeys.message(id) });
      }
      return;
    }
    case "label.changed":
    case "draft.changed": {
      queryClient.invalidateQueries({ queryKey: queryKeys.labels });
      queryClient.invalidateQueries({ queryKey: listKey });
      return;
    }
    case "sync.required": {
      queryClient.invalidateQueries();
      return;
    }
    default: {
      // Exhaustiveness guard: a new event type must be handled explicitly.
      const _exhaustive: never = event;
      void _exhaustive;
    }
  }
}

/** Human-readable announcement for a newly arrived message. */
export function describeEvent(event: MailboxEvent): string | null {
  if (event.type === "message.created") {
    const sender = event.message.from.name ?? event.message.from.address;
    return `New message from ${sender}: ${event.message.subject || "(no subject)"}`;
  }
  return null;
}
